/**
 * Menu is component that shows extra content when it is clicked.
 * Clicking it toggles visibility of MenuContent.
 *
 * Example:
 *  <Menu>
 *    <MenuLabel>
 *      <span>Open menu</span>
 *    </MenuLabel>
 *    <MenuContent>
 *      <MenuItem key="first item">
 *        <Button onClick={onClick}>Click this</Button>
 *      </MenuItem>
 *    </MenuContent>
 *  </Menu>
 *
 */

import { Box } from '@mui/material';
import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import MenuContent from './MenuContent/MenuContent';
import MenuLabel from './MenuLabel/MenuLabel';

const rootSx = {
  width: 'auto',
  height: '100%',
  position: 'relative',
  outline: 'none',
};

const KEY_CODE_ESCAPE = 27;
const CONTENT_PLACEMENT_OFFSET = 0;
const CONTENT_TO_LEFT = 'left';
const CONTENT_TO_RIGHT = 'right';
const MAX_MOBILE_SCREEN_WIDTH = 768;

const isControlledMenu = (isOpenProp, onToggleActiveProp) => {
  return isOpenProp !== null && onToggleActiveProp !== null;
};

/**
 * Menu component that toggles visibility of MenuContent when clicked.
 *
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {number} [props.contentPlacementOffset]
 * @param {('left'|'right')} [props.contentPosition]
 * @param {boolean|null} [props.isOpen] - When controlled, must be defined together with onToggleActive.
 * @param {Function|null} [props.onToggleActive] - When controlled, must be defined together with isOpen.
 * @param {boolean} [props.useArrow]
 * @param {boolean} [props.ready]
 * @param {React.ReactNode} props.children - Must be exactly MenuLabel and MenuContent.
 */
const MenuComponent = ({
  className = null,
  rootClassName = '',
  contentPlacementOffset = CONTENT_PLACEMENT_OFFSET,
  contentPosition = CONTENT_TO_RIGHT,
  isOpen: isOpenProp = null,
  onToggleActive = null,
  sx = null,
  useArrow = true,
  ready: readyProp = false,
  children,
}) => {
  const isIndependentMenu = isOpenProp === null && onToggleActive === null;
  if (!(isIndependentMenu || isControlledMenu(isOpenProp, onToggleActive))) {
    throw new Error(
      `Menu has invalid props:
          Both isOpen and onToggleActive need to be defined (controlled menu),
          or neither of them (menu uses its own state management).`
    );
  }

  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [ready, setReady] = useState(readyProp);

  const menuRef = useRef(null);
  const menuContentRef = useRef(null);

  useEffect(() => {
    // Menu needs to know about DOM before it can calculate it's size proberly.
    setReady(true);
  }, []);

  const isOpen = isControlledMenu(isOpenProp, onToggleActive) ? isOpenProp : internalIsOpen;

  const toggleOpen = useCallback(
    (enforcedState) => {
      // If state is handled outside of Menu component, we call a passed in onToggleActive func
      if (isControlledMenu(isOpenProp, onToggleActive)) {
        const isMenuOpen = enforcedState != null ? enforcedState : !isOpenProp;
        onToggleActive(isMenuOpen);
      } else {
        // If state is handled inside of Menu component, set state
        setInternalIsOpen((prevIsOpen) =>
          enforcedState != null ? enforcedState : !prevIsOpen
        );
      }
    },
    [isOpenProp, onToggleActive]
  );

  const onBlur = (event) => {
    // FocusEvent is fired faster than the link elements native click handler
    // gets its own event. Therefore, we need to check the origin of this FocusEvent.
    if (menuRef.current && !menuRef.current.contains(event.relatedTarget)) {
      if (isControlledMenu(isOpenProp, onToggleActive)) {
        onToggleActive(false);
      } else {
        setInternalIsOpen(false);
      }
    }
  };

  const onKeyDown = (e) => {
    // Gather all escape presses to close menu
    if (e.keyCode === KEY_CODE_ESCAPE) {
      toggleOpen(false);
    }
  };

  const positionStyleForMenuContent = (position) => {
    if (menuRef.current && menuContentRef.current) {
      const windowWidth = window.innerWidth;
      const rect = menuRef.current.getBoundingClientRect();

      // Calculate wether we should show the menu to the left of the component or right
      const distanceToRight = windowWidth - rect.right;
      const menuWidth = menuRef.current.offsetWidth;
      const contentWidthBiggerThanLabel = menuContentRef.current.offsetWidth - menuWidth;
      const usePositionLeftFromLabel = position === CONTENT_TO_LEFT;

      if (windowWidth <= MAX_MOBILE_SCREEN_WIDTH) {
        // Take full screen width on mobile
        return {
          left: -1 * (rect.left - 24),
          width: 'calc(100vw - 48px)',
        };
      }

      // Render menu content to the left according to the contentPosition
      // prop or if the content does not fit to the right. Otherwise render to
      // the right.
      return usePositionLeftFromLabel || distanceToRight < contentWidthBiggerThanLabel
        ? { right: contentPlacementOffset, minWidth: menuWidth }
        : { left: contentPlacementOffset, minWidth: menuWidth };
    }

    // When the MenuContent is rendered for the first time
    // (for the sake of width calculation),
    // move it outside of viewport to prevent possible overflow.
    return isOpen ? {} : { left: '-10000px' };
  };

  const positionStyleForArrow = (isPositionedRight) => {
    if (menuRef.current) {
      const menuWidth = menuRef.current.offsetWidth;
      return isPositionedRight
        ? Math.floor(menuWidth / 2) - contentPlacementOffset
        : Math.floor(menuWidth / 2);
    }
    return 0;
  };

  const prepareChildren = () => {
    if (React.Children.count(children) !== 2) {
      throw new Error('Menu needs to have two children: MenuLabel and MenuContent.');
    }

    return React.Children.map(children, (child) => {
      if (child.type === MenuLabel) {
        // MenuLabel needs toggleOpen function
        // We pass that directly  so that component user doesn't need to worry about that
        return React.cloneElement(child, {
          isOpen,
          onToggleActive: toggleOpen,
        });
      } else if (child.type === MenuContent) {
        // MenuContent needs some styling data (width, arrowPosition, and isOpen info)
        // We pass those directly so that component user doesn't need to worry about those.
        const positionStyles = positionStyleForMenuContent(contentPosition);
        const arrowPosition = useArrow
          ? positionStyleForArrow(positionStyles.right != null)
          : null;
        return React.cloneElement(child, {
          arrowPosition,
          contentRef: (node) => {
            menuContentRef.current = node;
          },
          isOpen,
          style: { ...child.props.style, ...positionStyles },
        });
      } else {
        throw new Error('Menu has an unknown child. Only MenuLabel and MenuContent are allowed.');
      }
    });
  };

  const classes = classNames(rootClassName, className);
  const menuChildren = ready ? prepareChildren() : null;

  return (
    <Box
      className={classes}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      ref={(c) => {
        menuRef.current = c;
      }}
      sx={[rootClassName ? undefined : rootSx, sx].filter(Boolean)}
      tabIndex={0}
    >
      {menuChildren}
    </Box>
  );
};

MenuComponent.displayName = 'Menu';

const Menu = memo(MenuComponent);

export default Menu;
